import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { Check, ArrowUpRight } from 'lucide-react';
import i1 from '@/assets/i1.jpg';
import i2 from '@/assets/i2.jpg';
import i3 from '@/assets/i3.jpg';

const reveal = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: 'easeOut' } }),
};

export const Entorn = () => {
  const { t } = useLang();

  return (
    <section
      id="entorn"
      data-testid="entorn-section"
      className="relative scroll-mt-20 py-20 sm:py-28 bg-[var(--cea-accent)] text-[#040f0c]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        {/* Sticky narrative */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <span className="text-xs font-bold tracking-[0.18em] text-[#040f0c] uppercase">{t.entorn.tag}</span>
            <h2 className="mt-3 font-display font-medium uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-wide text-[#040f0c]">
              {t.entorn.title}
            </h2>
            <p className="mt-6 text-[#040f0c]/85 leading-relaxed">{t.entorn.p1}</p>
            <p className="mt-4 text-[#040f0c]/85 leading-relaxed">{t.entorn.p2}</p>
            <ul className="mt-7 flex flex-wrap gap-2.5">
              {t.entorn.chips.map((c) => (
                <li key={c} className="inline-flex items-center gap-2 rounded-full border border-[#040f0c]/35 bg-[#040f0c]/5 px-3.5 py-1.5 text-sm font-semibold text-[#040f0c]">
                  <Check size={14} className="text-[#040f0c]" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Image grid: two square photos on top, one wide panorama below */}
        <div className="lg:col-span-7 lg:mt-[1.85rem]">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="grid grid-cols-2 gap-4 sm:gap-5"
          >
            <div className="group overflow-hidden rounded-2xl border border-[#040f0c]/20 bg-[#040f0c]">
              <img src={i1} alt="Sant Joan de Caselles, Canillo" loading="lazy" className="h-48 sm:h-64 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
            </div>
            <div className="group overflow-hidden rounded-2xl border border-[#040f0c]/20 bg-[#040f0c]">
              <img src={i3} alt="Vall de Canillo, Andorra" loading="lazy" className="h-48 sm:h-64 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
            </div>
          </motion.div>

          <motion.div
            variants={reveal}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="group overflow-hidden rounded-2xl border border-[#040f0c]/20 bg-[#040f0c] mt-4 sm:mt-5"
          >
            <img src={i2} alt="Canillo, Andorra" loading="lazy" className="h-44 sm:h-56 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
          </motion.div>

          {/* Visit Canillo CTA below the horizontal panorama */}
          <div className="flex justify-center mt-5 sm:mt-6">
            <a
              href="https://www.canillo.ad/"
              target="_blank"
              rel="noreferrer"
              data-testid="entorn-visit-btn"
              className="glow-btn inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--cea-accent)]"
            >
              {t.entorn.visitBtn}
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
